/**
 * SecurityBoundary Component
 * Page-level security wrapper that enforces permissions and logs access
 */

import React, { useEffect } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { logPageAccess, logSecurityViolation } from '../audit';
import { PermissionScope, SystemRole } from '@/types';

interface Permission {
  resource: string;
  action: string;
  scope?: PermissionScope;
}

interface SecurityBoundaryProps {
  children: React.ReactNode;
  requiredPermissions?: Permission[];
  requiredSystemRole?: SystemRole;
  auditPageAccess?: string;
  tenantIsolation?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const SecurityBoundary: React.FC<SecurityBoundaryProps> = ({
  children,
  requiredPermissions = [],
  requiredSystemRole,
  auditPageAccess,
  tenantIsolation = true
}) => {
  const { hasPermission, hasSystemRole, user, userProfile } = useAuth();
  const { currentTenant, isLoading: tenantLoading } = useTenant();

  // Log page access
  useEffect(() => {
    if (auditPageAccess && user) {
      logPageAccess({
        page: auditPageAccess,
        action: 'view'
      });
    }
  }, [auditPageAccess, user]);

  // CRITICAL: Wait for user profile to load before checking permissions/roles
  // This prevents the "Access Denied" flash when profile data is still loading
  const needsPermissionCheck = requiredPermissions.length > 0 || requiredSystemRole;

  if (needsPermissionCheck && !userProfile) {
    // Profile is still loading - show loading state instead of Access Denied
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div
            className='animate-spin rounded-full h-8 w-8 border-b-2
              border-primary mx-auto mb-4'
          />
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  // Check system role if required
  const hasSystemRoleAccess = requiredSystemRole ? hasSystemRole(requiredSystemRole) : true;

  // Check all required permissions
  const hasPermissionAccess =
    requiredPermissions.length === 0 ||
    requiredPermissions.every(perm =>
      hasPermission({
        resource: perm.resource,
        action: perm.action,
        scope: perm.scope || PermissionScope.SELF
      })
    );

  // Super admin bypasses tenant isolation
  const isSuperAdmin = hasSystemRole(SystemRole.SUPER_ADMIN);
  const shouldEnforceTenantIsolation = tenantIsolation && !isSuperAdmin;

  // Check tenant context if isolation is required (and user is not super admin)
  if (shouldEnforceTenantIsolation && !tenantLoading && !currentTenant?.id) {
    logSecurityViolation('missing_tenant_context', {
      page: auditPageAccess || 'unknown',
      userId: user?.id
    });
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <h2 className='text-2xl font-bold mb-4'>Tenant Context Error</h2>
          <p className='text-muted-foreground'>
            Unable to determine tenant context. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  // Check access (system role OR permissions)
  if (!hasSystemRoleAccess || !hasPermissionAccess) {
    logSecurityViolation('permission_denied', {
      page: auditPageAccess || 'unknown',
      requiredSystemRole,
      requiredPermissions,
      userId: user?.id,
      tenantId: currentTenant?.id
    });

    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <h2 className='text-2xl font-bold mb-4'>Access Denied</h2>
          <p className='text-muted-foreground'>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return children;
};
